import { z } from 'zod';
import { SUPPORTED_WHISPER_MODELS } from '../types/settings.js';
/**
 * Validates a candidate OpenAI API key shape.
 *
 * OpenAI keys look like `sk-...` (legacy) or `sk-proj-...` (project-scoped) or
 * `sk-svcacct-...` (service account) etc. We don't try to enforce the prefix
 * strictly — OpenAI is free to add new key shapes. Just guard against obvious
 * garbage and trim whitespace.
 */
export const SetOpenAIKeyRequestSchema = z.object({
    apiKey: z
        .string()
        .trim()
        .min(20, 'API key looks too short')
        .max(512, 'API key looks too long')
        .refine((s) => !/\s/.test(s), 'API key cannot contain whitespace'),
});
/**
 * Google OAuth 2.0 client credentials (created under the admin's own Google
 * Cloud project). Client IDs always end in `.apps.googleusercontent.com`;
 * secrets are opaque strings. We trim whitespace and reject anything
 * obviously malformed but defer hard validation to the live OAuth flow.
 */
export const SetGoogleOauthCredentialsRequestSchema = z.object({
    clientId: z
        .string()
        .trim()
        .min(20, 'Client ID looks too short')
        .max(512, 'Client ID looks too long')
        .refine((s) => !/\s/.test(s), 'Client ID cannot contain whitespace')
        .refine((s) => s.endsWith('.apps.googleusercontent.com'), 'Client ID must end with .apps.googleusercontent.com'),
    clientSecret: z
        .string()
        .trim()
        .min(10, 'Client secret looks too short')
        .max(512, 'Client secret looks too long')
        .refine((s) => !/\s/.test(s), 'Client secret cannot contain whitespace'),
});
/**
 * Buffer GraphQL API keys (from publish.buffer.com/settings/api). They're
 * long JWT-style strings; we accept anything plausible and reject whitespace.
 */
export const SetBufferTokenRequestSchema = z.object({
    apiKey: z
        .string()
        .trim()
        .min(10, 'Buffer token looks too short')
        .max(2048, 'Buffer token looks too long')
        .refine((s) => !/\s/.test(s), 'Token cannot contain whitespace'),
});
/**
 * Cloudflare R2 connection patch — any subset of fields. Pass empty string
 * to clear a field server-side. Account id and bucket name are non-secret
 * and round-trip plaintext; access key id and secret access key are
 * encrypted at rest.
 */
export const PatchR2SettingsRequestSchema = z
    .object({
    accountId: z
        .string()
        .trim()
        .max(64, 'Account ID looks too long')
        .optional(),
    accessKeyId: z
        .string()
        .trim()
        .max(128, 'Access key id looks too long')
        .optional(),
    secretAccessKey: z
        .string()
        .trim()
        .max(256, 'Secret access key looks too long')
        .optional(),
    bucketName: z
        .string()
        .trim()
        .max(64, 'Bucket name looks too long')
        .optional(),
    publicBaseUrl: z
        .string()
        .trim()
        .max(2048, 'Public base URL looks too long')
        .optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
/** Meta app secret — the per-app secret used to HMAC-verify inbound webhooks
 *  from Facebook / Instagram / WhatsApp. Treat as bearer-secret: encrypted
 *  at rest, never returned to the frontend. */
export const SetMetaAppSecretRequestSchema = z.object({
    apiKey: z
        .string()
        .trim()
        .min(8, 'Meta app secret looks too short')
        .max(512, 'Meta app secret looks too long')
        .refine((s) => !/\s/.test(s), 'App secret cannot contain whitespace'),
});
/** Webhook verify tokens are shared secrets we generate ourselves — Meta /
 *  WhatsApp echoes them on the subscription GET handshake. Stored as
 *  plaintext since they're already public over the wire on every callback. */
const VerifyTokenSchema = z.object({
    verifyToken: z
        .string()
        .trim()
        .min(8, 'Verify token must be at least 8 chars')
        .max(256, 'Verify token must be at most 256 chars')
        .refine((s) => !/\s/.test(s), 'Verify token cannot contain whitespace'),
});
export const SetMetaWebhookVerifyTokenRequestSchema = VerifyTokenSchema;
export const SetWhatsappWebhookVerifyTokenRequestSchema = VerifyTokenSchema;
/**
 * Voice-agent config patch — accepts URL and/or JSON template. Passing `null`
 * clears the field. Empty string is treated as null on the server side.
 */
export const PatchVoiceAgentConfigRequestSchema = z
    .object({
    webhookUrl: z
        .union([
        z
            .string()
            .trim()
            .max(2048, 'URL too long')
            .url('Must be a valid URL'),
        z.literal(''),
        z.null(),
    ])
        .optional(),
    jsonTemplate: z
        .union([z.string().max(8192, 'Template too long'), z.null()])
        .optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
});
export const PatchAppSettingsRequestSchema = z
    .object({
    defaultChatModel: z.string().trim().min(1).max(120).optional(),
    whisperModel: z.enum(SUPPORTED_WHISPER_MODELS).optional(),
    supportAiChatModel: z.string().trim().min(1).max(120).optional(),
    supportAiEmbeddingModel: z
        .enum(['text-embedding-3-small', 'text-embedding-3-large'])
        .optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
});
//# sourceMappingURL=settings.schema.js.map